import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapPin,
  Copy,
  ExternalLink,
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  Calendar,
  Building,
  FileText,
  Eye,
  LandPlot,
  Ruler,
  Activity,
} from "lucide-react";
import api from "../../../../utils/api";
import {
  useToast,
  Button,
  Card,
  EmptyState,
} from "../components/AdminSharedComponents";
import ReportModal from "../report/ReportModal";

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ShowExcavation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast(); 
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const [excavationPoint, setExcavationPoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // States for reports
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // State untuk menghindari refetch tidak perlu
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!id || hasLoaded) return;

    let isMounted = true;
    const controller = new AbortController();

    /* const fetchData = async () => {
      try {
        const excavationResponse = await api.get(
          `/api/excavation-points/${id}`,
          {
            signal: controller.signal,
          }
        );

        const reportsResponse = await api.get(
          `/api/reports/excavation-point/${id}`,
          {
            signal: controller.signal,
          }
        );

        if (isMounted) {
          setExcavationPoint(excavationResponse.data);
          setReports(reportsResponse.data);
          setHasLoaded(true);
        }
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Error fetching excavation point or reports:", error);
          toast.error("Failed to load excavation point or related reports");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }; */
    const fetchData = async () => {
      try {
        // Ambil excavation point
        const excavationResponse = await api.get(
          `/api/excavation-points/${id}`,
          {
            signal: controller.signal,
          }
        );

        let reportsData = [];
        try {
          // Ambil reports
          const reportsResponse = await api.get(
            `/api/reports/excavation-point/${id}`,
            { signal: controller.signal }
          );
          reportsData = reportsResponse.data;
        } catch (err) {
          // Kalau report 404 → biarkan kosong
          if (err.response && err.response.status === 404) {
            reportsData = [];
          } else {
            throw err; // error lain tetap dilempar
          }
        }

        if (isMounted) {
          // Handle the API response structure: { status: "success", data: {...} }
          const excavationData =
            excavationResponse.data.data || excavationResponse.data;
          setExcavationPoint(excavationData);
          setReports(reportsData);
          setHasLoaded(true);
        }
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Error fetching excavation point or reports:", error);
          toast.error("Failed to load excavation point");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id, hasLoaded, toast]);

  // Initialize map when excavation point data is loaded
  useEffect(() => {
    if (excavationPoint && mapRef.current && !mapInstanceRef.current) {
      const hasCoordinates =
        excavationPoint.coordinates?.lat && excavationPoint.coordinates?.lng;

      const defaultLat = hasCoordinates
        ? excavationPoint.coordinates.lat
        : -7.2504;
      const defaultLng = hasCoordinates
        ? excavationPoint.coordinates.lng
        : 112.7688;
      const defaultZoom = hasCoordinates ? 17 : 10;

      mapInstanceRef.current = L.map(mapRef.current).setView(
        [defaultLat, defaultLng],
        defaultZoom
      );

      // Define tile layers
      const cartoDBLayer = L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: "abcd",
          maxZoom: 20,
        }
      );

      const openStreetMapLayer = L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }
      );

      // Add CartoDB as default layer
      cartoDBLayer.addTo(mapInstanceRef.current);

      // Add layer control for switching
      const baseLayers = {
        "CartoDB Light": cartoDBLayer,
        OpenStreetMap: openStreetMapLayer,
      };
      L.control.layers(baseLayers).addTo(mapInstanceRef.current);

      if (hasCoordinates) {
        const marker = L.marker([defaultLat, defaultLng]).addTo(
          mapInstanceRef.current
        );
        marker.bindPopup(
          `<b>${excavationPoint.name}</b><br/>Depth: ${
            excavationPoint.depth ? excavationPoint.depth + "m" : "Not set"
          }<br/>Status: ${excavationPoint.status}`
        );
      }
    }
  }, [excavationPoint]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/api/excavation-points/${id}`);
      toast.success("Excavation point deleted successfully");
      navigate("/admin/projects/excavation/map");
    } catch (error) {
      console.error("Error deleting excavation point:", error);
      toast.error("Failed to delete excavation point");
    }
    setShowDeleteModal(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const baseStyle =
      "px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium";
    switch (status) {
      case "Completed":
        return `${baseStyle} bg-green-100 text-green-800`;
      case "In Progress":
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case "Planned":
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      case "On Hold":
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case "Cancelled":
        return `${baseStyle} bg-red-100 text-red-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800`;
    }
  };

  const handleCopyCoordinates = () => {
    if (excavationPoint.coordinates?.lat && excavationPoint.coordinates?.lng) {
      const lat = excavationPoint.coordinates.lat;
      const lng = excavationPoint.coordinates.lng;
      navigator.clipboard.writeText(`${lat}, ${lng}`);
      toast.success("Coordinates copied to clipboard!");
    }
  };

  const handleOpenInGoogleMaps = () => {
    if (excavationPoint.coordinates?.lat && excavationPoint.coordinates?.lng) {
      const lat = excavationPoint.coordinates.lat;
      const lng = excavationPoint.coordinates.lng;
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex justify-center items-center z-[9999] bg-gray-50">
        <div className="animate-pulse text-lg font-semibold text-primary">
          Loading excavation point...
        </div>
      </div>
    );
  }

  if (!excavationPoint) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4 flex-col justify-center items-center flex">
          <h2 className="text-xl font-semibold mb-4 text-primary-dark">
            Excavation point not found
          </h2>
          <Link to="/admin/projects/excavation/map">
            <Button icon={ArrowLeft}>Back to Map</Button>
          </Link>
        </div>
      </div>
    );
  }

  const backLink = `/admin/projects/excavation/map`;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      {/* Desktop Header - Sticky */}
      <div className="hidden md:block sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to={backLink}
                className="flex items-center gap-2 text-base text-primary hover:text-primary-dark transition-colors mr-6"
              >
                <ArrowLeft className="h-5 w-5" />
                Back to Excavation Points
              </Link>
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold text-primary-dark">
                  {excavationPoint.name}
                </h1>
                <span className={getStatusBadge(excavationPoint.status)}>
                  {excavationPoint.status}
                </span>
              </div>
            </div>

            {/* Desktop Action Buttons */}
            <div className="flex items-center gap-3">
              <Link
                to={`/admin/projects/excavation/edit/${excavationPoint.id}`}
              >
                <Button icon={Edit}>Edit Point</Button>
              </Link>
              <Button
                variant="danger"
                icon={Trash2}
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header - Sticky */}
      <div className="md:hidden sticky top-[72px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex justify-between items-center p-4">
          {/* Back Button */}
          <Link
            to={backLink}
            className="flex items-center gap-2 text-base text-primary hover:text-primary-dark transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="leading-none">Back</span>
          </Link>

          {/* Title + Badge */}
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-primary-dark">
              {excavationPoint.name}
            </h1>
            <span className={getStatusBadge(excavationPoint.status)}>
              {excavationPoint.status}
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Floating Action Buttons */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-lg">
        <div className="px-4 py-3">
          <div className="grid gap-2 grid-cols-2">
            <Button
              variant="danger"
              icon={Trash2}
              onClick={() => setShowDeleteModal(true)}
              className="w-full py-2"
            >
              Delete
            </Button>
            <Link to={`/admin/projects/excavation/edit/${excavationPoint.id}`}>
              <Button icon={Edit} className="w-full py-2">
                Edit Point
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-8">
            {/* Left Column - Excavation Point Details */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-primary-dark">
                  Excavation Point Details
                </h2>
              </div>

              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Point Name
                    </label>
                    <p className="text-base font-medium text-primary-dark break-words">
                      {excavationPoint.name}
                    </p>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                      <Building className="h-4 w-4" />
                      Project
                    </label>
                    <p className="text-sm sm:text-base text-primary-dark break-words">
                      {excavationPoint.Project?.name || "Unknown Project"}
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                      <Activity className="h-4 w-4" />
                      Status
                    </label>
                    <span className={getStatusBadge(excavationPoint.status)}>
                      {excavationPoint.status}
                    </span>
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                      <Ruler className="h-4 w-4" />
                      Depth
                    </label>
                    <p className="text-sm sm:text-base text-primary-dark">
                      {excavationPoint.depth
                        ? `${excavationPoint.depth}m`
                        : "Not specified"}
                    </p>
                  </div>
                </div>

                {excavationPoint.description && (
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                      <FileText className="h-4 w-4" />
                      Description
                    </label>
                    <p className="text-sm sm:text-base text-primary-dark break-words">
                      {excavationPoint.description}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 pt-4 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Created
                    </label>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {formatDate(excavationPoint.created_at)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Last Updated
                    </label>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {formatDate(excavationPoint.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Related Reports */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row sm:items-center md:justify-between gap-3 w-full">
                  <h2 className="text-lg font-semibold text-primary-dark">
                    {reports.length > 0
                      ? `Related Reports (${reports.length})`
                      : "No Related Reports"}
                  </h2>

                  <Link
                    to={`/admin/reports/add?excavation_point_id=${id}`}
                    className="sm:w-auto w-full"
                  >
                    <Button icon={Plus} className="w-full sm:w-auto">
                      Add Report
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                {reports.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 sm:gap-4 max-h-96 overflow-y-auto">
                    {reports.map((report) => (
                      <Card
                        key={report.id}
                        className="!p-3 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => {
                          setSelectedReport(report);
                          setIsModalOpen(true);
                        }}
                      >
                        <p className="text-sm sm:text-base font-medium text-primary-dark break-words">
                          {report.title}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">
                          {report.description}
                        </p>
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">
                              {new Date(report.created_at).toLocaleDateString()}
                            </span>
                          </span>
                          <div className="text-xs sm:text-sm inline-flex items-center text-primary-dark flex-shrink-0">
                            <Eye className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">
                              View Details
                            </span>
                            <span className="sm:hidden">View</span>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={FileText}
                    message="No reports associated with this excavation point yet."
                    buttonLabel="Create First Report"
                    buttonIcon={Plus}
                    buttonLink={`/admin/reports/add?excavation_point_id=${id}`}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Location & Map Section */}
          <div className="mt-4 sm:mt-8 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                <LandPlot className="h-5 w-5" />
                Point Location
              </h2>

              <div className="flex items-center gap-2">
                <Link to={`/admin/projects/show/${excavationPoint.project_id}`}>
                  <Button
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    <Building className="h-4 w-4" />
                    <span>View Project</span>
                  </Button>
                </Link>
              </div>
            </div>

            <div className="p-4 md:p-6">
              {excavationPoint.coordinates?.lat &&
              excavationPoint.coordinates?.lng ? (
                <div className="space-y-4 md:space-y-6">
                  {/* Mobile Coordinates Info - Only show on mobile */}
                  <div className="md:hidden bg-primary/5 rounded-lg border border-primary/20 overflow-hidden">
                    <div className="p-3 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <h4 className="text-sm sm:text-base font-semibold text-primary-dark">
                          GPS Coordinates
                        </h4>
                        <div className="flex flex-row items-stretch gap-2">
                          <Button
                            variant="secondary"
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1 text-xs px-2 py-1.5 h-auto"
                            onClick={handleCopyCoordinates}
                          >
                            <Copy className="w-3 h-3" />
                            <span className="hidden sm:inline">
                              Copy Coordinates
                            </span>
                            <span className="sm:hidden">Copy</span>
                          </Button>

                          <Button
                            variant="secondary"
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1 text-xs px-2 py-1.5 h-auto"
                            onClick={handleOpenInGoogleMaps}
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span className="hidden sm:inline">
                              Open G-Maps
                            </span>
                            <span className="sm:hidden">Open G-Maps</span>
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 text-xs sm:text-sm">
                            Latitude
                          </span>
                          <div className="bg-white/50 rounded-md px-2 py-1 mt-1 border border-gray-200">
                            <code className="font-mono text-xs sm:text-sm text-primary-dark break-all">
                              {excavationPoint.coordinates.lat.toFixed(6)}
                            </code>
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600 text-xs sm:text-sm">
                            Longitude
                          </span>
                          <div className="bg-white/50 rounded-md px-2 py-1 mt-1 border border-gray-200">
                            <code className="font-mono text-xs sm:text-sm text-primary-dark break-all">
                              {excavationPoint.coordinates.lng.toFixed(6)}
                            </code>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Map - Single container for both desktop and mobile */}
                  <div className="relative border border-gray-300 rounded-lg overflow-hidden shadow-inner">
                    <div ref={mapRef} className="h-[450px] rounded-lg z-[1]" />

                    {/* Desktop Coordinates overlay */}
                    <div className="hidden md:block absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-64 z-50">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-700">
                          GPS Coordinates
                        </h4>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Lat:</span>
                          <code className="font-mono text-gray-800 bg-gray-50 px-2 py-1 rounded">
                            {excavationPoint.coordinates.lat.toFixed(6)}
                          </code>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Lng:</span>
                          <code className="font-mono text-gray-800 bg-gray-50 px-2 py-1 rounded">
                            {excavationPoint.coordinates.lng.toFixed(6)}
                          </code>
                        </div>
                        <div className="flex gap-1 pt-1">
                          <Button
                            variant="primary"
                            className="flex-1 text-xs px-2 py-1 h-auto"
                            onClick={handleCopyCoordinates}
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                          <Button
                            variant="secondary"
                            className="flex-1 text-xs px-2 py-1 h-auto"
                            onClick={handleOpenInGoogleMaps}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Maps
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-4">
                    <MapPin className="h-full w-full" />
                  </div>
                  <h3 className="text-base sm:text-lg font-medium mb-2 text-primary-dark">
                    No Location Set
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-sm mx-auto px-4">
                    This excavation point doesn't have coordinates assigned yet.
                    Add location to see it on the map.
                  </p>
                  <Link to={`/admin/excavation-points/edit/${id}`}>
                    <Button icon={Plus} className="text-sm">
                      Add Location
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {isModalOpen && selectedReport && (
        <ReportModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          report={selectedReport}
          excavationPoint={excavationPoint}
        />
      )}

      {/* Delete Modal - Mobile Optimized */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-primary-dark">
                Delete Excavation Point
              </h3>
            </div>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              Are you sure you want to delete "
              <strong className="break-words">{excavationPoint.name}</strong>"?
              This action cannot be undone.
            </p>

            <div className="space-y-2 sm:space-y-3">
              <Button
                variant="danger"
                className="w-full text-sm sm:text-base py-2"
                onClick={handleDelete}
              >
                Delete Excavation Point
              </Button>

              <Button
                variant="secondary"
                className="w-full text-sm sm:text-base py-2"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowExcavation;
